import { useEffect, useState, useCallback } from "react";
import { Calendar, Plus, Trash2, Edit3, Bell, BellOff, AlarmClock, Brain } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { pushSupported, notificationPermission, enablePushNotifications, disablePushNotifications, currentPushSubscription } from "../lib/push";

const OFFSET_OPTIONS = [
  { v: 14, label: "2 weeks before" },
  { v: 7, label: "1 week before" },
  { v: 3, label: "3 days before" },
  { v: 1, label: "1 day before" },
  { v: 0, label: "On the day" },
];

function fmt(d) {
  try {
    const dt = new Date(d);
    return dt.toLocaleString(undefined, { weekday: "short", year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return d; }
}

function daysUntil(d) {
  const ms = new Date(d) - new Date();
  if (ms < -12 * 3600 * 1000) return null;
  if (ms < 0) return 0;
  return Math.ceil(ms / (24 * 3600 * 1000));
}

function TestDialog({ open, onClose, categories, initial, onSaved }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [date, setDate] = useState(initial?.test_date ? initial.test_date.slice(0, 16) : "");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [categoryId, setCategoryId] = useState(initial?.category_id || "");
  const [offsets, setOffsets] = useState(initial?.reminder_offsets || [7, 1, 0]);
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const toggleOffset = (v) => {
    setOffsets((cur) => cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v].sort((a, b) => b - a));
  };

  const save = async () => {
    if (!title.trim() || !date) { toast.error("Title and date required"); return; }
    setBusy(true);
    try {
      const iso = new Date(date).toISOString();
      const body = { title, test_date: iso, notes, category_id: categoryId || null, reminder_offsets: offsets };
      if (initial?.id) await api.put(`/tests/${initial.id}`, body);
      else await api.post("/tests", body);
      onSaved(); onClose();
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white border-2 border-slate-900 rounded-lg w-full max-w-lg p-6" style={{ boxShadow: "8px 8px 0 0 #0F172A" }} onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-2xl font-black">{initial ? "Edit test" : "New test"}</h3>
        <div className="mt-4 space-y-3">
          <div>
            <label className="text-xs uppercase font-bold tracking-widest">Title</label>
            <input data-testid="test-title-input" value={title} onChange={(e)=>setTitle(e.target.value)} className="nb-input mt-1" placeholder="Biology mock exam"/>
          </div>
          <div>
            <label className="text-xs uppercase font-bold tracking-widest">Date &amp; time</label>
            <input data-testid="test-date-input" type="datetime-local" value={date} onChange={(e)=>setDate(e.target.value)} className="nb-input mt-1"/>
          </div>
          <div>
            <label className="text-xs uppercase font-bold tracking-widest">Category</label>
            <select value={categoryId} onChange={(e)=>setCategoryId(e.target.value)} className="nb-input mt-1">
              <option value="">— none —</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {categoryId && <p className="text-xs text-slate-500 mt-1">Tap reminders will deep-link straight to this category's words.</p>}
          </div>
          <div>
            <label className="text-xs uppercase font-bold tracking-widest">Reminders</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {OFFSET_OPTIONS.map((o) => {
                const active = offsets.includes(o.v);
                return (
                  <button key={o.v} type="button" onClick={() => toggleOffset(o.v)}
                    className={`nb-tag text-xs ${active ? "bg-yellow-300" : "bg-white"}`}
                    data-testid={`test-offset-${o.v}`}>
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="text-xs uppercase font-bold tracking-widest">Notes</label>
            <textarea value={notes} onChange={(e)=>setNotes(e.target.value)} rows={2} className="nb-input mt-1" placeholder="Topics, room, study tips…"/>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={save} disabled={busy} data-testid="test-save-btn" className="nb-btn-primary flex-1">{busy ? "Saving…" : "Save"}</button>
          <button onClick={onClose} className="nb-btn-secondary">Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function Tests() {
  const [tests, setTests] = useState([]);
  const [cats, setCats] = useState([]);
  const [dialog, setDialog] = useState({ open: false, edit: null });
  const [pushState, setPushState] = useState({ supported: false, permission: "default", subscribed: false });

  const load = useCallback(async () => {
    const [t, c] = await Promise.all([api.get("/tests"), api.get("/categories")]);
    setTests(t.data); setCats(c.data);
  }, []);

  const refreshPush = useCallback(async () => {
    if (!pushSupported()) { setPushState({ supported: false, permission: "unsupported", subscribed: false }); return; }
    const sub = await currentPushSubscription();
    setPushState({ supported: true, permission: notificationPermission(), subscribed: !!sub });
  }, []);

  useEffect(() => { load(); refreshPush(); }, [load, refreshPush]);

  const enable = async () => {
    try {
      await enablePushNotifications();
      toast.success("Browser notifications enabled");
      refreshPush();
    } catch (e) { toast.error(e.message || "Could not enable"); }
  };

  const disable = async () => {
    await disablePushNotifications();
    toast.success("Notifications turned off on this device");
    refreshPush();
  };

  const del = async (id) => {
    if (!confirm("Delete this test?")) return;
    await api.delete(`/tests/${id}`);
    toast.success("Deleted");
    load();
  };

  const catName = (cid) => cats.find((c) => c.id === cid)?.name;
  const catColor = (cid) => cats.find((c) => c.id === cid)?.color || "#FFFFFF";

  const upcoming = tests.filter((t) => daysUntil(t.test_date) !== null);
  const past = tests.filter((t) => daysUntil(t.test_date) === null);

  return (
    <div className="p-6 sm:p-8 lg:p-12">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Test reminders</p>
          <h1 className="font-display text-4xl font-black mt-1">Test dates</h1>
          <p className="text-base font-medium text-slate-600 mt-2">Set your tests, pick when you want to be reminded, and we'll ping your device.</p>
        </div>
        <button onClick={() => setDialog({ open: true, edit: null })} data-testid="add-test-btn" className="nb-btn-primary inline-flex items-center gap-2">
          <Plus className="w-4 h-4" strokeWidth={2.5}/> Add test
        </button>
      </header>

      <section className="nb-card p-5 mb-6 grid sm:grid-cols-[1fr_auto] gap-4 items-center" style={{ background: "#7DD3FC33" }}>
        <div>
          <h2 className="font-display text-xl font-bold flex items-center gap-2">
            <Bell className="w-5 h-5" strokeWidth={2.5}/> Browser notifications
          </h2>
          <p className="text-sm text-slate-700 mt-1">
            {!pushState.supported && "This browser doesn't support push notifications."}
            {pushState.supported && pushState.permission === "denied" && "Permission was denied. Update site settings to re-enable."}
            {pushState.supported && pushState.permission !== "denied" && (pushState.subscribed
              ? "On — reminders will appear in your system notification bar even when this tab is closed."
              : "Off — turn on to get reminders in your phone/computer notification bar.")}
          </p>
        </div>
        {pushState.supported && (
          pushState.subscribed ? (
            <button onClick={disable} data-testid="push-disable-btn" className="nb-btn-secondary inline-flex items-center gap-2"><BellOff className="w-4 h-4" strokeWidth={2.5}/> Turn off</button>
          ) : (
            <button onClick={enable} disabled={pushState.permission === "denied"} data-testid="push-enable-btn" className="nb-btn-primary inline-flex items-center gap-2"><Bell className="w-4 h-4" strokeWidth={2.5}/> Turn on</button>
          )
        )}
      </section>

      {upcoming.length === 0 ? (
        <div className="nb-card p-12 text-center">
          <Calendar className="w-10 h-10 mx-auto" strokeWidth={2.5}/>
          <p className="font-display text-xl font-bold mt-3">No upcoming tests</p>
          <p className="text-sm text-slate-600">Add one above and you'll get reminders.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {upcoming.map((t) => {
            const d = daysUntil(t.test_date);
            const urgency = d <= 1 ? "#F9A8D4" : d <= 3 ? "#FDBA74" : d <= 7 ? "#FDE047" : "#86EFAC";
            return (
                <article key={t.id} className="nb-card p-5" style={{ background: urgency }} data-testid={`test-card-${t.title}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-display text-2xl font-bold">{t.title}</h3>
                      <p className="text-sm font-medium text-slate-800 mt-1 flex items-center gap-2">
                        <Calendar className="w-4 h-4" strokeWidth={2.5}/> {fmt(t.test_date)}
                      </p>
                    </div>
                    <span className="nb-tag bg-white text-sm whitespace-nowrap">
                      {d === 0 ? "Today" : d === 1 ? "1 day" : `${d} days`}
                    </span>
                  </div>
                  {t.category_id && (
                    <span className="nb-tag mt-3 inline-block" style={{ background: catColor(t.category_id) }}>{catName(t.category_id)}</span>
                  )}
                  {t.notes && <p className="text-sm text-slate-800 mt-3 leading-relaxed">{t.notes}</p>}
                  <div className="flex flex-wrap items-center gap-2 mt-3 text-xs font-bold text-slate-700">
                    <AlarmClock className="w-3 h-3" strokeWidth={2.5}/>
                    {t.reminder_offsets?.length
                      ? t.reminder_offsets.map((o) => (
                          <span key={o} className="nb-tag bg-white text-[10px]">
                            {o === 0 ? "On day" : `${o}d`}
                          </span>
                        ))
                      : <span>No reminders</span>}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {t.category_id && (
                      <a href={`/quiz?category_id=${t.category_id}&auto=1&n=5`} data-testid={`test-quiz-${t.title}`} className="nb-btn-primary text-xs inline-flex items-center gap-1 py-2 px-3"><Brain className="w-3 h-3" strokeWidth={2.5}/> Quiz me</a>
                    )}
                    <button onClick={() => setDialog({ open: true, edit: t })} className="nb-btn-secondary text-xs inline-flex items-center gap-1 py-2 px-3"><Edit3 className="w-3 h-3" strokeWidth={2.5}/> Edit</button>
                    <button onClick={() => del(t.id)} data-testid={`test-delete-${t.title}`} className="nb-btn-danger text-xs inline-flex items-center gap-1 py-2 px-3"><Trash2 className="w-3 h-3" strokeWidth={2.5}/> Delete</button>
                  </div>
                </article>
            );
          })}
        </div>
      )}

      {past.length > 0 && (
        <section className="mt-10">
          <h2 className="font-display text-2xl font-bold mb-4 text-slate-500">Past tests</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {past.map((t) => (
              <div key={t.id} className="nb-card-sm p-3 flex items-center justify-between gap-3 bg-white">
                <div>
                  <div className="font-bold">{t.title}</div>
                  <div className="text-xs text-slate-500">{fmt(t.test_date)}</div>
                </div>
                <button onClick={() => del(t.id)} className="nb-btn-danger text-xs inline-flex items-center gap-1 py-1.5 px-2"><Trash2 className="w-3 h-3" strokeWidth={2.5}/></button>
              </div>
            ))}
          </div>
        </section>
      )}

      <TestDialog open={dialog.open} onClose={() => setDialog({ open: false, edit: null })} categories={cats} initial={dialog.edit} onSaved={load}/>
    </div>
  );
}
